import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { GetWishlistData, RemoveWishlist } from "../../api/wishlistService";
import { useAuth } from "../../context/AuthContext";
import { useCount } from "../../context/CountContext";
import "../../css/Wishlist/wishlist.css";
import CustomerSelection from "../../components/Common/CustomerSelection";
import WishListIcon from "../../components/Common/wishListIcon";
import CartIcon from "../../components/Common/CartIcon";
import { TransferBasket } from "../../api/styleJewelleryService";

const DEFAULT_IMAGE = "/assets/images/VaultAccess/no-image3.png";

export default function Wishlist() {
    document.title = "Wishlist | Meera Jewellery House Admin";
    const { user, showToast } = useAuth();
    const navigate = useNavigate();
    const { fetchWishlistCount, fetchStyleCartCount, wishlistCount } = useCount();
    const [wishlistItems, setWishlistItems] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const groupedData = useMemo(() => {
        const groups = {};
        wishlistItems.forEach(item => {
            const customerName = item.CustomerName || "Unknown Customer";
            if (!groups[customerName]) {
                groups[customerName] = [];
            }
            groups[customerName].push(item);
        });
        return groups;
    }, [wishlistItems]);

    const onUpdateWishlist = () => {
        fetchWishlistData();
    };

    const fetchWishlistData = async () => {
        try {
            setIsLoading(true);
            const payload = {
                Customer_ID: user?.SelectedCustomer_ID || "0",
                StockCategory: "STYLE",
                BasketType: "WISHLIST"
            };
            const data = await GetWishlistData(payload);
            setWishlistItems(data?.WishlistItems || data?.ROW || data?.Row || []);
        } catch (error) {
            console.error("Failed to fetch wishlist:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchWishlistData();
    }, [user?.SelectedCustomer_ID]);

    const confirmDelete = async () => {
        if (!itemToDelete) return;

        if (!user?.SelectedCustomer_ID || user?.SelectedCustomer_ID === 0) {
            showToast({
                title: "Selection Required",
                message: "Please select a specific customer to remove items.",
                type: "warning"
            });
            return;
        }

        try {
            const res = await RemoveWishlist({
                Party_ID: user?.SelectedCustomer_ID,
                Cart_ID: (itemToDelete.Cart_ID || itemToDelete.Wishlist_ID || itemToDelete.Basket_ID)?.toString(),
                Style_ID: itemToDelete.Style_ID
            });

            if (res?.Message?.[0]?.RETURNCODE === "1" || res?.ZMESSAGE?.[0]?.RETURNCODE === "1") {
                const { RETURNMESSAGE, RETURNMESSAGETITLE } = res.Message[0];
                fetchWishlistCount(user?.SelectedCustomer_ID);
                fetchWishlistData();
                setShowDeleteModal(false);
                setItemToDelete(null);
                showToast({
                    title: RETURNMESSAGETITLE || "Success",
                    message: RETURNMESSAGE || "Item removed from Wishlist.",
                    type: "success"
                });
            }
        } catch (error) {
            console.error("Delete failed:", error);
        }
    };

    const handleTransferToCart = async (item) => {
        try {
            const res = await TransferBasket({
                Party_ID: user?.SelectedCustomer_ID,
                Style_ID: item.Style_ID
            });

            if (res?.Message?.[0]?.RETURNCODE === "1") {
                const { RETURNMESSAGE, RETURNMESSAGETITLE } = res.Message[0];
                fetchWishlistCount(user?.SelectedCustomer_ID);
                fetchStyleCartCount(user?.SelectedCustomer_ID);
                fetchWishlistData();
                showToast({
                    title: RETURNMESSAGETITLE || "Success",
                    message: RETURNMESSAGE || "Item transferred to cart.",
                    type: "success"
                });
            } else {
                showToast({
                    title: "Error",
                    message: res?.Message?.[0]?.RETURNMESSAGE || "Failed to transfer item.",
                    type: "error"
                });
            }
        } catch (error) {
            console.error("Transfer failed:", error);
        }
    };

    const handleLocalUpdate = useCallback((Style_ID) => {
        setWishlistItems(prevItems => {
            const exists = prevItems.some(item => item.Style_ID === Style_ID);
            if (!exists) return prevItems;
            return prevItems.filter(item => item.Style_ID !== Style_ID);
        });
    }, []);

    const handleCartTransfer = async (item) => {
        handleLocalUpdate(item.Style_ID);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        }) + " " + date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const deleteModalFooter = (
        <div className="wishlist-modal-footer">
            <button
                onClick={() => setShowDeleteModal(false)}
                className="wishlist-modal-btn-secondary"
            >
                Keep it
            </button>
            <button
                onClick={confirmDelete}
                className="wishlist-modal-btn-danger"
            >
                <i className="pi pi-trash" style={{ fontSize: '0.9rem' }} />
                Remove Item
            </button>
        </div>
    );

    const isTrue = (val) => ["YES", "TRUE", "1", "Y"].includes(String(val ?? "").toUpperCase());

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }
        },
        exit: {
            opacity: 0,
            y: 20,
            transition: { duration: 0.2 }
        }
    };

    const renderWishlistItem = (item, index) => (
        <motion.div
            key={(item.Cart_ID || item.Wishlist_ID || item.Basket_ID) || index}
            initial="hidden"
            animate="show"
            exit="exit"
            variants={itemVariants}
            className="wishlist-item-exact wishlist-cards"
        >
            <div className="item-left-visual">
                <Link to={`/detail?style_ID=${item.Style_ID}`} style={{ display: 'block', height: '100%' }}>
                    <img
                        src={item.Image1 || DEFAULT_IMAGE}
                        alt={item.StyleNo}
                        onError={(e) => { e.currentTarget.src = DEFAULT_IMAGE; }}
                    />
                </Link>
            </div>

            <div className="item-details-container">
                <div className="item-info-col">
                    <h2 className="item-title-exact">{item.Line1 || "Untitled Design"}</h2>
                    <div className="item-ref-code">
                        Jewel Code: <span>{item.JewelCode}</span>
                    </div>
                    <div className="item-ref-code">
                        Style No: <span>{item.StyleNo}</span>
                    </div>
                    <div className="item-clock-date">
                        <i className="pi pi-clock" style={{ fontSize: '0.9rem' }} />
                        {formatDate(item.InsertDatetime || item.CartEntryDate)}
                    </div>
                </div>

                <div className="spec-column">
                    <div className="spec-title-exact">Product Details</div>
                    <div className="spec-row-exact">
                        <span className="spec-label-exact">Purity</span>
                        <span className="spec-value-exact">{item.MetalQuality || "-"}</span>
                    </div>
                    <div className="spec-row-exact">
                        <span className="spec-label-exact">Gold Wt</span>
                        <span className="spec-value-exact">{item.TotalNetWt ? `${item.TotalNetWt} g` : "-"}</span>
                    </div>
                    <div className="spec-row-exact">
                        <span className="spec-label-exact">Dimension</span>
                        <span className="spec-value-exact">{item.ItemSize || "-"}</span>
                    </div>
                </div>

                <div className="spec-column">
                    <div className="spec-title-exact">
                        {item.CDiaWt ? "Stone Specs" : "Diamond Details"}
                    </div>
                    <div className="spec-row-exact">
                        <span className="spec-label-exact">Weight</span>
                        <span className="spec-value-exact">
                            {item.DiaPcs || 0} Pcs / {item.DiaWt || 0} Cts
                        </span>
                    </div>
                    <div className="spec-row-exact">
                        <span className="spec-label-exact">Gross Wt</span>
                        <span className="spec-value-exact">{item.GrossWt ? `${item.GrossWt} g` : "-"}</span>
                    </div>
                    <div className="spec-row-exact">
                        <span className="spec-label-exact">Color</span>
                        <span className="spec-value-exact">{item.MetalTone || "-"}</span>
                    </div>
                </div>

                <div className="item-actions-col">
                    <div className="item-price-exact">
                        {item.LineAmount || item.TotalAmount}
                    </div>
                    <div className="item-action-btns-wrapper">
                        <CartIcon
                            fill="#0f4c81"
                            Style_ID={item.Style_ID}
                            JewelCode={item.JewelCode}
                            StyleNo={item.StyleNo}
                            isincart={item.IS_AddedInCart}
                            iswishlist="YES"
                            Cart_ID={item.Wishlist_ID || item.Basket_ID}
                            onTransfer={() => fetchWishlistData()}
                            isProcessing={isProcessing}
                            setIsProcessing={setIsProcessing}
                            size={28}
                        />
                        <WishListIcon
                            Style_ID={item.Style_ID}
                            Quantity={item.Quantity}
                            Cart_ID={item.Wishlist_ID || item.Basket_ID || item.Cart_ID}
                            onUpdateWishlistBasketID={() => handleLocalUpdate(item.Style_ID)}
                            iswishlist={isTrue(item.IS_AddedInWishlist) ? "YES" : "NO"}
                            isProcessing={isProcessing}
                            setIsProcessing={setIsProcessing}
                            size={28}
                            fill="#0f4c81"
                        />
                    </div>
                </div>
            </div>
        </motion.div>
    );

    return (
        <div className="wishlist-page">
            <div className="wishlist-header-card">
                <div className="wishlist-header-top-row">
                    <h1 className="wishlist-page-title">
                        Wishlist <span>({wishlistCount} items)</span>
                    </h1>
                    <div className="wishlist-header-actions-right">
                        <div className="header-actions-group">
                            <div className="cart-header-actions-left">
                                <CustomerSelection />
                            </div>
                            <button className="back-btn" onClick={() => window.history.back()} aria-label="Go back">
                                <i className="pi pi-arrow-left"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {wishlistItems.length === 0 && !isLoading ? (
                <div className="empty-wishlist-premium">
                    <div className="empty-wishlist-visual">
                        <i className="pi pi-heart" />
                    </div>
                    <div className="empty-wishlist-content">
                        <h2 className="empty-wishlist-title">Your Wishlist is Empty</h2>
                        <p className="empty-wishlist-text">
                            Looks like you haven't added anything to your wishlist yet.
                            Explore our collections to find your perfect match.
                        </p>
                        <Link to="/StyleInventory" className="btn-primary">
                            <i className="pi pi-compass me-2" />
                            Browse Collections
                        </Link>
                    </div>
                </div>
            ) : (
                (Number(user?.SelectedCustomer_ID || 0) === 0) ? (
                    <div className="accordion-full-width">
                        <AnimatePresence>
                            {isLoading ? (
                                <div className="skeleton-list">
                                    {Array.from({ length: 3 }).map((_, i) => (
                                        <div className="wishlist-item-exact skeleton-item" key={i}>
                                            <Skeleton width="100%" height="160px" borderRadius="12px" />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <Accordion multiple={true} className="customer-accordion">
                                    {Object.entries(groupedData).map(([customerName, items]) => (
                                        <AccordionTab
                                            key={customerName}
                                            header={
                                                <div className="accordion-header-content">
                                                    <span>{customerName}</span>
                                                    <span className="customer-badge">{items.length} items</span>
                                                </div>
                                            }
                                        >
                                            <div className="customer-items-list">
                                                {items.map((item, index) => renderWishlistItem(item, index))}
                                            </div>
                                        </AccordionTab>
                                    ))}
                                </Accordion>
                            )}
                        </AnimatePresence>
                    </div>
                ) : (
                    <div className="wishlist-layout">
                        <div className="wishlist-main-section">
                            <div className="wishlist-items-list-exact">
                                <AnimatePresence>
                                    {isLoading ? (
                                        <div className="skeleton-list">
                                            {Array.from({ length: 3 }).map((_, i) => (
                                                <div className="wishlist-item-exact skeleton-item" key={i}>
                                                    <Skeleton width="100%" height="160px" borderRadius="12px" />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="customer-items-list">
                                            {wishlistItems.map((item, index) => renderWishlistItem(item, index))}
                                        </div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                )
            )}

            <Dialog
                visible={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                footer={deleteModalFooter}
                draggable={false}
                resizable={false}
                showHeader={false}
                maskClassName="wishlist-modal-mask"
                className="wishlist-premium-modal"
                dismissableMask
            >
                <div className="wishlist-modal-content-wrapper">
                    <button className="modal-close-x" onClick={() => setShowDeleteModal(false)}>
                        <i className="pi pi-times" />
                    </button>

                    <div className="wishlist-modal-header-visual">
                        <div className="wishlist-modal-icon-bg">
                            <i className="pi pi-heart-fill" />
                        </div>
                        <h2 className="wishlist-modal-title">Remove from Wishlist?</h2>
                        <p className="wishlist-modal-subtitle">This item will be removed from your curated collection.</p>
                    </div>

                    {itemToDelete && (
                        <div className="wishlist-modal-product-preview">
                            <div className="preview-image-container">
                                <img
                                    src={itemToDelete.Image1 || DEFAULT_IMAGE}
                                    alt={itemToDelete.StyleNo}
                                    onError={(e) => { e.currentTarget.src = DEFAULT_IMAGE; }}
                                />
                            </div>
                            <div className="preview-details">
                                <div className="preview-name">{itemToDelete.Line1 || "Untitled Design"}</div>
                                <div className="preview-style">Style: {itemToDelete.StyleNo}</div>
                                <div className="preview-code">Code: {itemToDelete.JewelCode}</div>
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
}
